<template>
  <div class="full-width d-flex flex-column">
    <!-- 1) Trestle-type tabs -->
    <div class="pa-4 d-flex align-center header-row">
      <div class="header-toggles-wrapper">
        <v-btn-toggle
          v-model="selectedOptionIds"
          multiple
          mandatory
          color="primary"
        >
          <v-btn
            v-for="opt in baseTrestleOptions"
            :key="opt.id"
            :value="opt.id"
            small
            @click="loadTrestles(opt.id)"
          >
            {{ opt.type }}
          </v-btn>
        </v-btn-toggle>
      </div>

      <v-spacer />

      <v-btn
        class="ml-4 flex-shrink-0"
        color="primary"
        :loading="exportLoading"
        :disabled="exportLoading || !firstActiveOption"
        @click="requestTrestleExport"
      >
        Export
      </v-btn>
    </div>

    <!-- 2) Trestles container with loading overlay -->
    <div
      v-if="activeOptions.length"
      class="flex-1 d-flex overflow-x-auto flex-scroll-height"
      style="position: relative"
    >
      <!-- One column per active option -->
      <div
        v-for="opt in activeOptions"
        :key="opt.id"
        class="d-flex flex-column"
        style="min-width: 400px"
      >
        <!-- Optional column header -->
        <div class="px-2 pb-2 font-weight-medium d-flex align-center">
          <span>{{ opt.type }}</span>
          <v-spacer />
          <!-- Per-column loading indicator -->
          <v-progress-circular
            v-if="trestlesLoading[opt.id]"
            color="primary"
            indeterminate
            size="18"
            width="2"
          />
        </div>

        <div
          v-if="trestlesLoading[opt.id] && opt.trestles.length === 0"
          class="flex-1 d-flex align-center justify-center pa-4"
        >
          <v-progress-circular color="primary" indeterminate />
        </div>

        <!-- Trestles list (always show cached or fresh if available) -->
        <div
          v-else-if="opt.trestles.length > 0"
          class="d-flex flex-column ant-border-right flex-1 pa-2 flex-scroll-height"
        >
          <div class="flex-1 mx-2 overflow-y-auto pa-4">
            <!-- Render full trestle with all 5 positions -->
            <div
              v-for="(trestle, idx) in opt.trestles"
              :key="idx"
              class="d-flex ant-border-bottom pb-2 mb-2"
            >
              <div class="d-flex align-center justify-center">
                {{ idx + 1 }}
              </div>
              <div class="d-flex flex-column flex-1 align-end mr-2">
                <v-tooltip
                  v-for="module in [
                    ...new Set(
                      trestle.positions
                        .flatMap((p) => p.elements)
                        .map((e) => e?.module_id)
                    ),
                  ]"
                  :key="module"
                  left
                >
                  <template #activator="{ on }">
                    <v-chip
                      :color="getModuleColor(module, trestle)"
                      :style="{
                        opacity:
                          selectedModule !== null
                            ? selectedModule === module
                              ? 1
                              : 0.3
                            : 1,
                      }"
                      class="mb-1"
                      small
                      @click="toggleModule(module)"
                      v-on="on"
                    >
                      {{ module }}
                    </v-chip>
                  </template>
                  <span>{{ getTypeByModuleId(module, trestle) }}</span>
                </v-tooltip>
              </div>
              <base-dhme-single-trestle
                :selected-module="selectedModule"
                :trestle="trestle"
                :type-color-dictionary="typeColorDictionary"
                class="mb-5"
              />
            </div>
          </div>
        </div>

        <!-- No trestles fallback when not loading -->
        <div v-else class="flex-1 d-flex align-center justify-center pa-4">
          <span>No trestles found for this type.</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { executeCustomModuleCall } from '@/services/api/module.api';
import { mapGetters } from 'vuex';
import {
  getModuleColor,
  getModuleTypeByElements,
} from '@/modules/daiwa-house-modular-europe/AssemblyHallDashboard/assembly-hall-trestles.utils';
import BaseDhmeSingleTrestle from '@/components/Modules/Daiwa-House-Modular-Europe/Base/BaseDhmeSingleTrestle.vue';
import ColorHelper from '@/services/color-helper';

export default {
  name: 'AutomatedTrestleOverview',
  components: { BaseDhmeSingleTrestle },
  props: {
    selectedProject: { type: Object, required: true },
  },
  data() {
    return {
      selectedModule: null,
      baseTrestleOptions: [
        {
          id: 'north',
          type: 'Header walls (Position 1)',
          position: ['1'],
          trestles: [],
        },
        {
          id: 'south',
          type: 'Header walls (Position 2)',
          position: ['2'],
          trestles: [],
        },
        {
          id: 'west',
          type: 'Side walls (Position 3)',
          position: ['3'],
          trestles: [],
        },
        {
          id: 'east',
          type: 'Side walls (Position 4)',
          position: ['4'],
          trestles: [],
        },
        {
          id: 'inside',
          type: 'Inside walls (Position 5)',
          position: ['5'],
          trestles: [],
        },
        {
          id: 'exterior',
          type: 'Exterior walls',
          position: ['8'],
          trestles: [],
        },
        {
          id: 'roofs',
          type: 'Roofs (Position 6)',
          position: ['6'],
          trestles: [],
        },
      ],
      selectedOptionIds: [],
      trestlesCache: {},
      typeColorDictionary: {},
      isLoading: false,
      exportLoading: false,
      trestleLoadTimer: {},
      trestlesLoading: {},
      trestleLastLoad: {},
    };
  },
  computed: {
    ...mapGetters({
      project: 'project',
      moduleId: 'dhmeAssemblyHallDashboardStore/moduleId',
    }),
    activeOptions() {
      return this.baseTrestleOptions.filter((opt) =>
        this.selectedOptionIds.includes(opt.id)
      );
    },
    firstActiveOption() {
      return this.activeOptions[0] || null;
    },
  },
  watch: {
    selectedProject: {
      immediate: true,
      handler() {
        this.trestlesCache = {};
        const first = this.baseTrestleOptions[0];
        this.selectedOptionIds = [first.id];
        this.loadTrestles(first.id);
      },
    },
  },
  methods: {
    async requestTrestleExport() {
      if (!this.firstActiveOption) return;
      this.exportLoading = true;
      try {
        await executeCustomModuleCall(
          this.project.id,
          this.moduleId,
          'requestTrestleExport',
          {
            project: this.selectedProject.id,
          }
        );
        this.$store.commit('showNotification', {
          content:
            'Trestle export requested, You will be notified when your download is ready',
          color: 'info',
        });
      } catch (err) {
        this.$store.commit('showNotification', {
          content: 'Something went wrong while requesting the trestle export',
          color: 'error',
        });
      } finally {
        this.exportLoading = false;
      }
    },

    // Always reload on (re)toggle of a tab
    loadTrestles(optionId) {
      this.$set(this.trestlesLoading, optionId, true);
      this.loadTrestlesImmediate(optionId);
    },

    async loadTrestlesImmediate(optionId) {
      const option = this.baseTrestleOptions.find((opt) => opt.id === optionId);
      this.isLoading = true;
      try {
        const fresh = await this.fetchForPosition(option.position[0]);
        this.commitTrestles(option, fresh);
      } finally {
        this.isLoading = false;
        this.$set(this.trestlesLoading, optionId, false);
      }
    },

    toggleModule(module) {
      this.selectedModule = this.selectedModule === module ? null : module;
    },
    fetchForPosition(pos) {
      return executeCustomModuleCall(
        this.project.id,
        this.moduleId,
        'fetchTrestles',
        { project: this.selectedProject.id, position: pos }
      );
    },

    // centralize how fresh data is applied
    commitTrestles(option, fresh) {
      option.trestles = fresh;
      this.updateColorDictionary(fresh);
    },

    updateColorDictionary(freshTrestles) {
      const allElems = freshTrestles.flatMap((t) =>
        t.positions.flatMap((p) => p.elements)
      );
      const fetchedTypes = [...new Set(allElems.map((e) => e.module_type))];

      const newTypes = fetchedTypes.filter(
        (t) => !(t in this.typeColorDictionary)
      );

      const newDict = {};
      newTypes.forEach((type) => {
        newDict[type] = ColorHelper.generateLightColorHex();
      });

      this.typeColorDictionary = {
        ...this.typeColorDictionary,
        ...newDict,
      };
    },
    getModuleColor(mid, trestle) {
      return getModuleColor(mid, trestle, this.typeColorDictionary);
    },
    getTypeByModuleId(mid, trestle) {
      return getModuleTypeByElements(mid, trestle);
    },
  },
};
</script>

<style lang="scss" scoped>
.header-row {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.header-toggles-wrapper {
  max-width: 100%;
  overflow-x: auto;
  flex: 1 1 auto;
}

/* Compact toggle buttons so all trestle-type options fit on a normal screen
   without the toggle row needing to scroll. The overflow-x above stays as a
   fallback for very narrow viewports. */
.header-toggles-wrapper ::v-deep .v-btn {
  min-width: 0;
  padding: 0 8px !important;
  font-size: 0.7rem;
  letter-spacing: normal;
  text-transform: none;
}

/* prevent the export button from shrinking out of view */
.flex-shrink-0 {
  flex-shrink: 0;
}
</style>
